import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, ArrowLeft } from "lucide-react";
import SEO from "../components/SEO";
import NextPage from "../components/NextPage";
import { getColoringPages } from "../lib/api";

export default function Coloring() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  useEffect(() => {
    getColoringPages()
      .then((data) => setPages(data || []))
      .catch(() => setPages([]))
      .finally(() => setLoading(false));
  }, []);

  const resolveUrl = (u) =>
    u.startsWith("http") || u.startsWith("/coloring/") ? u : `${backendUrl}${u}`;

  return (
    <div className="parchment min-h-screen pt-32 pb-24 px-6" data-testid="coloring-page">
      <SEO
        title="Saltie Coloring Pages"
        description="Free downloadable coloring pages from The Salty Moo™ — Scripture-inspired line art for little hands."
        path="/coloring"
      />

      <div className="max-w-5xl mx-auto">
        <Link
          to="/story-barn"
          data-testid="back-to-story-barn"
          className="inline-flex items-center gap-2 text-[#6D5A50] hover:text-[#4B3A2F] transition-colors mb-8"
        >
          <ArrowLeft size={16} />
          <span className="font-body text-sm uppercase tracking-widest">
            Back to the Story Barn
          </span>
        </Link>

        <div className="text-center mb-14">
          <p className="text-xs uppercase tracking-[0.4em] text-[#4B3A2F] mb-3">
            The Story Barn · Printables
          </p>
          <h1 className="font-display text-5xl md:text-6xl text-[#4B3A2F] mb-4">
            Saltie Coloring Pages
          </h1>
          <div className="gold-rule mx-auto mb-6" />
          <p className="font-body text-lg text-[#2C2724]/85 max-w-2xl mx-auto leading-relaxed">
            Free, Scripture-inspired coloring pages for little hands. Print
            them out, gather up the crayons, and let the pasture come to life.
          </p>
        </div>

        {loading ? (
          <p className="text-center text-[#6D5A50]">Loading pages…</p>
        ) : pages.length === 0 ? (
          <p className="text-center text-[#6D5A50] italic">
            No coloring pages yet — new ones are on the way.
          </p>
        ) : (
          <div
            className="grid gap-8"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(20rem, 1fr))" }}
            data-testid="coloring-gallery"
          >
            {pages.map((p) => {
              const url = resolveUrl(p.file_url);
              return (
                <article
                  key={p.slug}
                  data-testid={`coloring-card-${p.slug}`}
                  className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md overflow-hidden flex flex-col"
                >
                  <div className="bg-white p-3">
                    <img
                      src={url}
                      alt={p.title}
                      className="w-full h-64 object-contain"
                      loading="lazy"
                      draggable="false"
                    />
                  </div>
                  <div className="p-5 flex flex-col flex-grow">
                    {p.verse && (
                      <p className="text-xs uppercase tracking-[0.25em] text-[#8A7B6D] mb-1">
                        {p.verse}
                      </p>
                    )}
                    <h2 className="font-display text-2xl text-[#4B3A2F] leading-tight mb-4">
                      {p.title}
                    </h2>
                    <a
                      href={url}
                      download
                      data-testid={`download-${p.slug}`}
                      className="mt-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#4B3A2F] text-[#F7F0E4] hover:bg-[#3E2F24] transition-colors font-body text-sm uppercase tracking-widest"
                    >
                      <Download size={16} />
                      Download for Printing
                    </a>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <p className="text-center text-sm text-[#6D5A50] italic mt-14 max-w-xl mx-auto">
          More coloring pages are on the way — new pastures, new critters,
          new Scripture. Come back and visit the barn.
        </p>
      </div>
    </div>
  );
}
